import React, { useContext, useState, useCallback, useEffect } from 'react';
import { Accordion, Icon, Menu, Checkbox, Statistic, Dropdown, Table, Segment, Button, Label } from 'semantic-ui-react'
import { GroupReservationContext, actions } from '../context'
import _ from 'lodash'
import moment from 'moment'
import FitReservation from './fit';
import { Currencies } from '../../../components/shared/currencies';
import {
  SaveReservation, UpsertGroupBlock,
  UpdateReservationStatus, UpdateReservationColor, GetRooms
} from '../../../api/services/pms';
import { Products } from '../../../api/services/next';
import { onError, onWarning, onSuccess } from '../../../components/shared/messages';
import { useLoader } from '../../../hooks/useLoader';
import { resStates, activeBlocks, getMainBlock, isInactiveReservation } from '../consts';
import { dateFormatSpaces, GuestAccountType, ReservationAmountOverrideType } from '../../../components/shared/constants'
import { useTextResources } from '../../../language';
import uniqid from 'uniqid'
import CancelReservationPopup from '../../../components/CancelReservationPopup';


const FitTab = () => {
  const loader = useLoader()
  const { state, dispatch } = useContext(GroupReservationContext)
  const reservations = state.ReservationGroup.ReservationGroupReservations
  // Classic can cancel a block and leave its FITs active; those FITs stay editable, the block gets no new FITs and is never upserted
  const blocks = _.filter(reservations, r =>
    r.IsBlock && (!isInactiveReservation(r) || _.some(reservations, ['ParentReservationID', r.Reservation.Id])))
  const [active, setActive] = useState(_.map(blocks, () => false))
  const [selectedRooms, setSelectedRooms] = useState([])
  const [errors, setErrors] = useState({})
  const [cancelPopupOpen, setCancelPopupOpen] = useState(false)
  const [reservationsToCancel, setReservationsToCancel] = useState([])
  const text = useTextResources()

  useEffect(() => {
    _.forEach(reservations, reservation => {
      if (!reservation.IsBlock && reservation.Id) {
        reservation.OriginalQuantity = reservation.Reservation.Quantity
        reservation.OriginalAdults = reservation.Reservation.Adults
        reservation.OriginalChildren = reservation.Reservation.Children
        reservation.OriginalInfants = reservation.Reservation.Infants
      }
    })
  }, [])

  // Fetch RateName for FIT reservations that don't have it yet
  // Group by ParentReservationID (block) since all FITs in a block share the same product
  useEffect(() => {
    const fetchRateNames = async () => {
      const fitsNeedingRateName = reservations.filter(r => 
        !r.IsBlock && r.Reservation?.ProductID && !r.Reservation.RateName
      )
      if (fitsNeedingRateName.length === 0) return

      // Group FITs by their parent block - all FITs under same block share same ProductID
      const byBlock = _.groupBy(fitsNeedingRateName, fit => fit.ParentReservationID)
      
      const blockPromises = Object.values(byBlock).map(async (fits) => {
        const firstFit = fits[0].Reservation
        const products = await Products.Search({
          HotelId: firstFit.HotelId,
          RoomTypeId: firstFit.RoomTypeId,
          FromDate: moment(firstFit.CheckInDate).startOf('day').toISOString(),
          ToDate: moment(firstFit.CheckOutDate).startOf('day').toISOString(),
          Id: firstFit.ProductID,
          Includes: ["Rate"]
        })
        if (products?.length > 0) {
          // Apply RateName to all FITs in this block
          fits.forEach(fit => {
            fit.Reservation.RateName = products[0].RateName
          })
        }
      })
      await Promise.all(blockPromises)
      dispatch({ type: actions.update })
    }
    fetchRateNames()
  }, [])

  useEffect(() => {
    dispatch({
      type: actions.registerTabSave, index: 3, callback: async () => {
        if (!validate()) return;

        loader(true);
        /* eslint-disable require-atomic-updates */
        let tasks = []
        let success_fits = []
        _.forEach(blocks, async block => {
          const block_reservation = block.Reservation
          const fitReservations = _.filter(reservations, fit => {
            return fit.ParentReservationID === block_reservation.Id && fit.State && fit.State != resStates.saved
          })
          if (fitReservations.length) {
            _.forEach(fitReservations, async fit => {
              fit.State = resStates.saving
              dispatch({ type: actions.update })
              try {
                if (fit.Reservation) {
                  if (fit.Reservation.ReservationPricingTypeID != ReservationAmountOverrideType.Proportional) {
                    fit.Reservation.IsOverriddenTotalAmountAfterTax = false
                    fit.Reservation.OverrideProductPrice = true
                  } else {
                    // For Proportional pricing, ensure OverrideProductPrice is set and OverriddenTotalAmount
                    // reflects the current (possibly recalculated) price before saving
                    fit.Reservation.OverrideProductPrice = true
                    fit.Reservation.OverriddenTotalAmount = fit.Reservation.IsOverriddenTotalAmountAfterTax
                      ? parseFloat(fit.Reservation.TotalAmountProductCurrentAfterTax)
                      : parseFloat(fit.Reservation.TotalAmountProductCurrentBeforeTax)
                  }
                }
                const task = SaveReservation(fit.Reservation)
                tasks.push(task)
                const savedReservation = await task;
                // Preserve display-only fields before merge (API may return null/empty)
                const rateName = fit.Reservation.RateName
                const roomTypeName = fit.Reservation.RoomTypeName
                _.mergeWith(fit.Reservation, savedReservation,
                  (a, b) => b === null ? a : undefined
                )
                // Restore display-only fields if they were cleared by the merge
                if (!fit.Reservation.RateName && rateName) fit.Reservation.RateName = rateName
                if (!fit.Reservation.RoomTypeName && roomTypeName) fit.Reservation.RoomTypeName = roomTypeName
                fit.ReservationID = fit.Reservation.Id
                fit.State = resStates.saved
                fit.Error = null
                success_fits.push(fit)
                block_reservation.Quantity = block_reservation.Quantity - (fit.Reservation.Quantity - fit.OriginalQuantity)
                block_reservation.Adults = block_reservation.Adults - (fit.Reservation.Adults - fit.OriginalAdults)
                block_reservation.Children = block_reservation.Children - (fit.Reservation.Children - fit.OriginalChildren)
                block_reservation.Infants = block_reservation.Infants - (fit.Reservation.Infants - fit.OriginalInfants)

                // ovverride case take price from ovveride price
                if (block_reservation.ReservationPricingTypeID === ReservationAmountOverrideType.AmountByNight) {
                  block_reservation.OverriddenTotalAmount =
                    Math.max(0, parseFloat(block_reservation.TotalAmountProductCurrentBeforeTax) - parseFloat(fit.Reservation.TotalAmountProductCurrentBeforeTax))

                  block_reservation.IsOverriddenTotalAmountAfterTax = false
                  block_reservation.OverrideProductPrice = true
                }
              } catch (error) {
                fit.State = resStates.error
                fit.Error = error
              }
            })
          }
          else {
            // Send OverriddenTotalAmount for non fit blocks
            if (block_reservation.ReservationPricingTypeID === ReservationAmountOverrideType.AmountByNight) {
              block_reservation.OverriddenTotalAmount = parseFloat(block_reservation.TotalAmountProductCurrentBeforeTax);
              block_reservation.IsOverriddenTotalAmountAfterTax = false
              block_reservation.OverrideProductPrice = true
            }
          }
        })
        await Promise.allSettled(tasks)
        // Now that we saved all reservations let's update the blocks
        if (success_fits.length) {
          try {
            // Update the group in the database
            const updatedGroup = await UpsertGroupBlock({
              ...state.ReservationGroup, ReservationGroupReservations: [
                ...activeBlocks(reservations),
                // Clear the reservation object from fit in order not to save them again with the group
                ..._.map(success_fits, fit => {
                  return {
                    ...fit,
                    Reservation: null
                  }
                })]
            })
            _.forEach(updatedGroup.ReservationGroupReservations, reservationGroupReservation => {
              let res = _.find(reservations, r => { return r.Reservation.Id === reservationGroupReservation.ReservationID })
              if (res) {
                if (!res.Id) res.Id = reservationGroupReservation.Id;
                // Preserve display-only fields and price fields before merge.
                // The group response may contain stale price data (fetched before SaveReservation committed),
                // so we always preserve the prices that were just saved.
                const rateName = res.Reservation.RateName
                const roomTypeName = res.Reservation.RoomTypeName
                const priceBeforeTax = res.Reservation.TotalAmountProductCurrentBeforeTax
                const priceAfterTax = res.Reservation.TotalAmountProductCurrentAfterTax
                const wasSaved = _.some(success_fits, f => f.Reservation.Id === reservationGroupReservation.ReservationID)
                _.mergeWith(res.Reservation, reservationGroupReservation.Reservation,
                  (a, b) => b === null ? a : undefined
                )
                // Restore display-only fields if they were cleared by the merge
                if (!res.Reservation.RateName && rateName) res.Reservation.RateName = rateName
                if (!res.Reservation.RoomTypeName && roomTypeName) res.Reservation.RoomTypeName = roomTypeName
                // Restore prices for just-saved FIT reservations to prevent stale group response
                // from overwriting the correctly saved prices
                if (wasSaved) {
                  res.Reservation.TotalAmountProductCurrentBeforeTax = priceBeforeTax
                  res.Reservation.TotalAmountProductCurrentAfterTax = priceAfterTax
                }
              }
            })
            _.forEach(success_fits, fit => {
              fit.OriginalQuantity = fit.Reservation.Quantity
              fit.OriginalAdults = fit.Reservation.Adults
              fit.OriginalChildren = fit.Reservation.Children
              fit.OriginalInfants = fit.Reservation.Infants
            })
            onSuccess()
          } catch (error) {
            error.status === 400 ? onWarning(error) : onError(error);
          } finally {
            loader(false);
          }
        } else {
          loader(false);
        }

        //no await, excecuted in background
        updateColors();
        // Update the UI with new data
        dispatch({ type: actions.update })
        // Refresh totals after saving
        dispatch({ type: actions.refreshTotals })

        return false
      }
    });
    dispatch({ type: actions.setDirty, payload: false });
  }, [])

  const updateColors = async () => {
    const reservations =
      state.ReservationGroup.ReservationGroupReservations
        .filter(r => (!r.Reservation.Color || r.Reservation.Color !== state.ReservationGroup.Color) && r.Reservation.Id && !isInactiveReservation(r))
        .map(r => {
          return {
            Id: r.Reservation.Id,
            HotelId: r.Reservation.HotelId,
            Color: state.ReservationGroup.Color
          }
        });

    if (reservations && reservations.length > 0) {
      const promises = reservations.map(r => UpdateReservationColor(r));
      return Promise.all(promises);
    }
  };

  const validate = () => {
    let errors = {}

    _.forEach(blocks, block => {
      const block_reservation = block.Reservation
      const fitReservations = _.filter(reservations, ["ParentReservationID", block_reservation.Id])
      _.forEach(fitReservations, (fit, index) => {
        const id = fit.ParentReservationID + "_" + index
        errors[id] = {}
        const reservation = fit.Reservation
        if (!reservation.CheckInDate || !reservation.CheckOutDate) {
          errors[id]["Dates"] = "Dates are required"
        }
        if (!reservation.GuestId && !reservation.Guest) {
          errors[id]["Guest"] = "Guest is required"
        }
        if (reservation.Adults + reservation.Children === 0) {
          errors[id]["PAX"] = "Adults or Children should not be zero"
        }
      })
    })
    setErrors(errors)
    let shouldSave = true
    _.forEach(errors, error => {
      if (!_.isEmpty(error)) shouldSave = false
    })
    return shouldSave
  }

  const _createFit = (block, pax) => {
    let roomTypeName = block.RoomTypeName
    if (!roomTypeName) {
      const roomType = _.find(state.RoomTypes, ['Id', block.RoomTypeId])
      roomTypeName = roomType?.Name
    }
    
    return {
      IsBlock: false,
      IsPrimaryReservationInGroup: false,
      ParentReservationID: block.Id,
      ReservationGroupID: state.ReservationGroup.Id,
      State: resStates.new,
      OriginalQuantity: 0,
      OriginalAdults: 0,
      OriginalChildren: 0,
      OriginalInfants: 0,
      Reservation: {
        ...block,
        Id: 0,
        Number: null,
        Guest: null,
        GuestId: null,
        Quantity: 1,
        Adults: pax.Adults,
        Children: pax.Children,
        Infants: pax.Infants,
        IsBlockReservaton: false,
        IsGroupReservation: true,
        // take ReservationPricingTypeID from block
        //ReservationPricingTypeID: 1,
        RenderFoliosImmediately: true,
        CreateFolio: true,
        TotalAmountProductCurrentBeforeTax: block.ReservationPricingTypeID === ReservationAmountOverrideType.AmountByNight ? parseFloat(block.AmountProductCurrentBeforeTax) : 0,
        TotalAmountProductCurrentAfterTax: block.ReservationPricingTypeID === ReservationAmountOverrideType.AmountByNight ? parseFloat(block.AmountProductCurrentAfterTax) : 0,
        OverriddenTotalAmount: block.ReservationPricingTypeID === ReservationAmountOverrideType.AmountByNight ? parseFloat(block.AmountProductCurrentBeforeTax) : 0,
        IsOverriddenTotalAmountAfterTax: false,
        OverrideProductPrice: true,
        ReservationStatus: null,
        RoomTypeName: roomTypeName
      }
    }
  }

  const addNew = block => {
    // Calculate the original block capacity by adding current quantity + existing individual reservations
    const existingFits = _.filter(reservations, reservation => { 
      return reservation.ParentReservationID === block.Id 
    })
    const savedFitQuantity = _.sumBy(existingFits, fit => fit.Reservation?.Id ? fit.Reservation.Quantity : 0)
    const originalBlockCapacity = block.Quantity + savedFitQuantity
    
    // Validate that we don't exceed original block capacity
    const totalCurrentFits = _.sumBy(existingFits, fit => fit.Reservation?.Quantity || 0)
    
    if (totalCurrentFits >= originalBlockCapacity) {
      onWarning({ "error": { "ErrorMessage": `Cannot add new reservation: Block already has ${totalCurrentFits} individual reservations out of ${originalBlockCapacity} original capacity.` } })
      return
    }

    let fit = _createFit(block, {
      Adults: originalBlockCapacity ? Math.floor(block.Adults / originalBlockCapacity) : 0,
      Children: originalBlockCapacity ? Math.floor(block.Children / originalBlockCapacity) : 0,
      Infants: originalBlockCapacity ? Math.floor(block.Infants / originalBlockCapacity) : 0,
    })
    reservations.push(fit)
    dispatch({ type: actions.update })
  }

  const autoSplit = block => {
    // Calculate the original block capacity by adding current quantity + existing individual reservations
    const existingFits = _.filter(reservations, reservation => { 
      return reservation.ParentReservationID === block.Id 
    })
    const savedFitQuantity = _.sumBy(existingFits, fit => fit.Reservation?.Id ? fit.Reservation.Quantity : 0)
    const originalBlockCapacity = block.Quantity + savedFitQuantity
    
    // Validate that we don't exceed original block capacity
    const existingFitQuantity = _.sumBy(existingFits, fit => fit.Reservation?.Quantity || 0)
    
    if (existingFitQuantity >= originalBlockCapacity) {
      onWarning({ "error": { "ErrorMessage": `Cannot auto-split: Block already has ${existingFitQuantity} individual reservations out of ${originalBlockCapacity} original capacity.` } })
      return
    }

    let newFits = _.filter(reservations, reservation => { return reservation.ParentReservationID === block.Id && !reservation.Reservation.Id })
    let quantity = block.Quantity - newFits.length
    let adults = block.Adults - _.sumBy(newFits, fit => fit.Reservation.Adults)
    let childrens = block.Children - _.sumBy(newFits, fit => fit.Reservation.Children)
    let infants = block.Infants - _.sumBy(newFits, fit => fit.Reservation.Infants)
    
    // Additional validation: ensure we don't exceed original block capacity
    const totalExistingQuantity = existingFitQuantity + newFits.length
    if (totalExistingQuantity + quantity > originalBlockCapacity) {
      quantity = Math.max(0, originalBlockCapacity - totalExistingQuantity)
      if (quantity === 0) {
        onWarning({ "error": { "ErrorMessage": `Cannot create more individual reservations: Original block capacity of ${originalBlockCapacity} rooms reached.` } })
        return
      }
    }

    const adultsPerRoom = quantity ? Math.floor(adults / quantity) : 0;
    const childrenPerRoom = quantity ? Math.floor(childrens / quantity) : 0;
    const infantsPerRoom = quantity ? Math.floor(infants / quantity) : 0;
    let restAdults = adults - adultsPerRoom * quantity;
    let restChildren = childrens - childrenPerRoom * quantity;
    let restInfants = infants - infantsPerRoom * quantity;

    _.times(quantity, () => {
      let fit =
        _createFit(block, {
          Adults: adultsPerRoom + (restAdults ? 1 : 0),
          Children: childrenPerRoom + (restChildren ? 1 : 0),
          Infants: infantsPerRoom + (restInfants ? 1 : 0)
        })
      if (restAdults) restAdults--
      if (restChildren) restChildren--
      if (restInfants) restInfants--
      reservations.push(fit)
    })
    dispatch({ type: actions.update })
  }

  const filterRooms = useCallback((rooms, currentValue, reservation) => {
    const retval = _.filter(rooms, room => {

      //This code check if room already assigned by room id and overlapping dates
      const alreadyAssigned = _.some(selectedRooms, r => {
        let startDate1 = moment(r.startDate)
        let endDate1 = r.startDate === r.endDate ? moment(r.endDate) : moment(r.endDate).add(-1, 'days')
        let startDate2 = moment(reservation.CheckInDate)
        let endDate2 = reservation.CheckInDate === reservation.CheckOutDate ? moment(reservation.CheckOutDate) : moment(reservation.CheckOutDate).add(-1, 'days')
        return (
          r.roomId === room.value &&
          ((startDate1.startOf('day') <= endDate2.startOf('day')) && (startDate2.startOf('day') <= endDate1.startOf('day')))
        )
      })
      return !alreadyAssigned || currentValue === room.value
    })
    return retval
  }, [selectedRooms])

  const onSelectRoom = (res, value, key) => {
    let keyExists = _.find(selectedRooms, s => s.key === key)
    // If fit already exists in selectedRooms list so update record, otherwise add new record to selectedRooms list
    if (keyExists) {
      keyExists.roomId = value
      setSelectedRooms([
        ...selectedRooms
      ])
    }
    else {
      setSelectedRooms([
        ...selectedRooms,
        {
          roomId: value,
          startDate: res.CheckInDate,
          endDate: res.CheckOutDate,
          key: key
        }
      ])
    }
  }

  const selectAllReservations = (checked, fitReservations) => {
    _.forEach(fitReservations, fit => {
      fit.IsSelected = checked
    })
    dispatch({ type: actions.update })
  }

  const deleteReservations = async block_reservation => {
    const selected = _.filter(reservations, fit => 
      fit.ParentReservationID === block_reservation.Id && fit.IsSelected && fit.Reservation.Id !== 0
    );

    if (selected.length === 0) return;

    const selectedReservations = selected.map(fit => fit.Reservation);
    setReservationsToCancel(selectedReservations)
    setCancelPopupOpen(true)
  }

  const handleCancelPopupClose = (updatedReservations) => {
    setCancelPopupOpen(false)
    if (updatedReservations) {
      const updatedArray = Array.isArray(updatedReservations) ? updatedReservations : [updatedReservations];
      updatedArray.forEach(updatedReservation => {
        // HS-63118: mirror the server-side block rebalance for the cancel direction only
        // (consuming -> non-consuming). This handler is reached exclusively from the cancel
        // flow, so we always add the slot back to the parent. Do NOT reuse this helper for
        // uncancel without inverting the delta — the server handles both directions, but
        // this code path covers cancel only.
        const cancelledFit = _.find(reservations, fit => fit.Reservation && fit.Reservation.Id === updatedReservation.Id)
        if (cancelledFit && cancelledFit.ParentReservationID) {
          const parentBlock = _.find(reservations, rgr => rgr.IsBlock && rgr.Reservation && rgr.Reservation.Id === cancelledFit.ParentReservationID)
          if (parentBlock) {
            parentBlock.Reservation.Quantity = (parentBlock.Reservation.Quantity || 0) + (cancelledFit.Reservation.Quantity || 0)
          }
        }
        _.remove(reservations, fit => fit.Reservation.Id === updatedReservation.Id)
      });
      dispatch({ type: actions.update })
      dispatch({ type: actions.refreshTotals })
    }
    setReservationsToCancel([])
  }

  const cancelChanges = block_reservation => {
    _.remove(reservations, fit => {
      return fit.ParentReservationID === block_reservation.Id && fit.Reservation.Id === 0
    })
    setErrors({})
    dispatch({ type: actions.update })
  }
  const fillGuests = ()=>{
    const mainReservation = getMainBlock(state.ReservationGroup.ReservationGroupReservations)
    const mainGuest = mainReservation.Reservation.Guest
    blocks.forEach((block, block_idx)=>{
      const block_reservation = block.Reservation
      const fitReservations = reservations.filter(x=>x.ParentReservationID === block_reservation.Id)
      fitReservations.forEach((fit, idx)=>{
        let guest = {
          Id: uniqid("newguest-"),
          FirstName: `${block_idx}_${idx}`,
          LastName: state.ReservationGroup.Name,
          GuestAccountTypeId: GuestAccountType.Normal
        }
        if (mainGuest) {
            guest.NationalityID = mainGuest.NationalityID
            guest.LanguageID = mainGuest.LanguageID
        }
        if (!fit.Reservation.GuestId) {
          fit.Reservation.GuestId = guest.Id
          fit.Reservation.Guest = guest
          fit.State = resStates.new
        }
      })
    })
    dispatch({ type: actions.update })
  }
  const fillRooms = async ()=>{
    const newSelectedRooms = []
    
    // Collect all reservation data that needs rooms
    const reservationsNeedingRooms = []
    
    for (const block of blocks) {
      const block_reservation = block.Reservation
      const fitReservations = reservations.filter(x=>x.ParentReservationID === block_reservation.Id)
      
      fitReservations.forEach((fit, index) => {
        const rowKey = block_reservation.Id + "_" + index
        if (!fit.Reservation.RoomId) {
          reservationsNeedingRooms.push({
            fit,
            rowKey,
            block_reservation
          })
        }
      })
    }
    
    // If no reservations need rooms, exit early
    if (reservationsNeedingRooms.length === 0) {
      return
    }
    
    // Group reservations by room type, hotel, and date range to minimize API calls
    const groupedReservations = _.groupBy(reservationsNeedingRooms, ({ block_reservation }) => 
      `${block_reservation.RoomTypeId}_${block_reservation.CheckInDate}_${block_reservation.CheckOutDate}`
    )
    
    // Process each group separately
    for (const group of Object.values(groupedReservations)) {
      const firstReservation = group[0]
      
      try {
        const rooms = await GetRooms({
          HotelID: state.ReservationGroup.HotelID,
          RoomTypeIDs: firstReservation.block_reservation.RoomTypeId,
          AvailableFrom: firstReservation.block_reservation.CheckInDate,
          AvailableTo: firstReservation.block_reservation.CheckOutDate
        })
        
        // Filter out rooms that are already selected to avoid conflicts
        const availableRooms = rooms.filter(room => 
          !selectedRooms.some(selected => 
            selected.roomId === room.Id &&
            moment(selected.startDate).isSame(firstReservation.block_reservation.CheckInDate, 'day') &&
            moment(selected.endDate).isSame(firstReservation.block_reservation.CheckOutDate, 'day')
          )
        )
        
        // Assign rooms sequentially to reservations in this group
        group.forEach(({ fit, rowKey }, index) => {
          if (index < availableRooms.length) {
            const room = availableRooms[index]
            fit.Reservation.RoomId = room.Id
            fit.Reservation.Room = room
            newSelectedRooms.push({
              roomId: room.Id,
              startDate: fit.Reservation.CheckInDate,
              endDate: fit.Reservation.CheckOutDate,
              key: rowKey
            })
            fit.State = resStates.new
          }
        })
      } catch (error) {
        console.error('Error fetching rooms for group:', error)
      }
    }
    
    setSelectedRooms([
      ...selectedRooms,
      ...newSelectedRooms
    ])
    dispatch({ type: actions.update })
  }

  return <React.Fragment>
    <Menu borderless secondary>
      <Menu.Item>
        <Checkbox toggle onChange={(e, { checked }) => {
          setActive(prev => {
            _.fill(prev, checked)
            return [...prev]
          })
        }} label={text("ViewExpanded")} checked={active.every(v => v)} />
      </Menu.Item>
      <Menu.Item>
        <Button onClick={()=>fillGuests()}>{text("FillGuestNames")}</Button>
      </Menu.Item>
      <Menu.Item>
        <Button onClick={()=>fillRooms()}>{text("FillRooms")}</Button>
      </Menu.Item>
    </Menu>
    <div className="fit">
      {
        _.map(blocks, (block, index) => {
          const block_reservation = block.Reservation
          const fitReservations = _.filter(reservations, ["ParentReservationID", block_reservation.Id])
          const block_value = parseFloat(block_reservation.TotalAmountProductCurrentAfterTax) + _.sumBy(fitReservations, fit => (fit.Reservation.Id !== 0) ? parseFloat(fit.Reservation.TotalAmountProductCurrentAfterTax) : 0)
          const currency = Currencies[block_reservation.CurrencyId]
          const savedFitReservations = _.filter(fitReservations, f => { return f.Reservation.Id })
          const block_cancelled = isInactiveReservation(block)
          const disableAddFit = block_cancelled || fitReservations.length >= (Math.max(0, block_reservation.Quantity) + savedFitReservations.length)
          // Calculate total PAX: only sum saved individual reservation guests
          const totalBlockAdults = _.sumBy(fitReservations, fit => (fit.Reservation.Id !== 0) ? Number(fit.Reservation.Adults) : 0)
          const totalBlockChildren = _.sumBy(fitReservations, fit => (fit.Reservation.Id !== 0) ? Number(fit.Reservation.Children) : 0)
          const totalBlockInfants = _.sumBy(fitReservations, fit => (fit.Reservation.Id !== 0) ? Number(fit.Reservation.Infants) : 0)
          return <Accordion fluid key={block_reservation.Id}>
            <Accordion.Title as={Table} attached={(active && active[index]) ? "top" : null} active={active ? active[index] : false} onClick={() => setActive(prev => {
              prev[index] = !prev[index]
              return [...prev]
            })
            }>
              <Table.Body>
                <Table.Row>
                  <Table.Cell width="2">
                    <Icon name="dropdown" />
                    {moment(block_reservation.CheckInDate).format(dateFormatSpaces)}-{moment(block_reservation.CheckOutDate).format(dateFormatSpaces)}
                  </Table.Cell>
                  <Table.Cell width="2">
                    {block_reservation.RoomTypeName || 'Room Type'}
                  </Table.Cell>
                  <Table.Cell width="2">
                    <Statistic size="mini">
                      <Statistic.Label>{text('Blocked') + "/" + text('Picked')}</Statistic.Label>
                      <Statistic.Value>{Math.max(0, Number(block_reservation.Quantity)) + _.sumBy(fitReservations, fit => (fit.Reservation.Id !== 0) ? fit.Reservation.Quantity : 0)}/{_.sumBy(fitReservations, fit => (fit.Reservation.Id !== 0) ? fit.Reservation.Quantity : 0)}</Statistic.Value>
                    </Statistic>
                  </Table.Cell>
                  <Table.Cell width="2">
                    {text('Pax')} {totalBlockAdults}/{totalBlockChildren}/{totalBlockInfants}
                  </Table.Cell>
                  <Table.Cell width="3">
                    {text('ResNum')} {block_reservation.Number}
                    {block_cancelled && <Label size="mini" color="grey" style={{ marginLeft: 6 }}>{text('Cancelled')}</Label>}
                  </Table.Cell>
                  <Table.Cell textAlign="right" width="2">
                    <Statistic size="mini">
                      <Statistic.Label>{text('GrossDailyRate')}</Statistic.Label>
                      <Statistic.Value>{isNaN(block_value) ? 0 :  block_value.toFixed(2)} {currency ? currency.symbol : ""}</Statistic.Value>
                    </Statistic>
                  </Table.Cell>
                  <Table.Cell textAlign="right" width="1">
                    <Dropdown item icon={{ name: "ellipsis vertical", size: "large" }} pointing="top right">
                      <Dropdown.Menu>
                        <Dropdown.Item disabled={disableAddFit} icon="plus square outline" text={text("AddNew")} onClick={() => {
                          addNew(block_reservation)
                          setActive(prev => {
                            prev[index] = true
                            return [...prev]
                          })
                        }} />
                        <Dropdown.Item disabled={disableAddFit} icon="clone outline" text={text("AutoSplit")} onClick={() => {
                          autoSplit(block_reservation)
                          setActive(prev => {
                            prev[index] = true
                            return [...prev]
                          })
                        }} />
                        <Dropdown.Item icon="trash alternate outline" text={text("Delete")} disabled={_.every(fitReservations, fit => fit.IsSelected === false)} onClick={() => deleteReservations(block_reservation)} />
                        <Dropdown.Item icon="undo" text={text("CancelChanges")} disabled={_.every(fitReservations, fit => fit.Reservation.Id !== 0)} onClick={() => cancelChanges(block_reservation)} />
                      </Dropdown.Menu>
                    </Dropdown>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Accordion.Title>
            {fitReservations.length ? (
              <Accordion.Content as={Table} celled size="small" attached="bottom" active={active ? active[index] : false}>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={{ width: "1em" }}><Checkbox onChange={(e, { checked }) => selectAllReservations(checked, fitReservations)} checked={_.every(fitReservations, "IsSelected")} /></Table.HeaderCell>
                    <Table.HeaderCell width="1">{text("ReservationNumber")}</Table.HeaderCell>
                    <Table.HeaderCell width="2">{text("Arrival")} - {text("Departure")}</Table.HeaderCell>
                    <Table.HeaderCell width="2">{text("Guest")}</Table.HeaderCell>
                    <Table.HeaderCell width="1">{text("Adults")}</Table.HeaderCell>
                    <Table.HeaderCell width="1">{text("Children")}</Table.HeaderCell>
                    <Table.HeaderCell width="1">{text("Infants")}</Table.HeaderCell>
                    <Table.HeaderCell width="2">{text("RoomType")}</Table.HeaderCell>
                    <Table.HeaderCell width="1">{text("RateName")}</Table.HeaderCell>
                    <Table.HeaderCell width="2">{text("Room")}</Table.HeaderCell>
                    <Table.HeaderCell width="2">{text("Product")} {text("PriceBeforeTax")}</Table.HeaderCell>
                    <Table.HeaderCell width="2">{text("Product")} {text("PriceAfterTax")}</Table.HeaderCell>
                    <Table.HeaderCell style={{ width: "1em" }}></Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {
                    _.map(fitReservations, (fit, index) => {
                      const key = block_reservation.Id + "_" + index
                      return <FitReservation fit={fit} filter={filterRooms} key={key} rowKey={key} onSelectRoom={onSelectRoom} errors={errors[key]} />
                    })
                  }
                </Table.Body>
              </Accordion.Content>
            ) : (
              <Accordion.Content as={Segment} active={active ? active[index] : false} textAlign="center" attached="bottom">
            {text('NoIndividualReservationsCreated')}
              </Accordion.Content>
            )
            }
          </Accordion>
        })
      }
    </div>
    <CancelReservationPopup
      open={cancelPopupOpen}
      reservations={reservationsToCancel}
      skipLoadState={true}
      onClose={handleCancelPopupClose}
    />
  </React.Fragment>
}
export default FitTab
